/**
 * Reading the citation proxy's answer (ACOP-100).
 *
 * Extracted from CitationTwo.tsx, which has done this since ACOP-93, because Knowledge Base (beta)
 * now renders articles too and has to reach exactly the same verdicts. The branch ORDER below is
 * load-bearing and non-obvious — see classifyArticleResponse — and a second hand-written copy of it
 * would drift, quietly, in the direction of telling readers an article is missing when it is not.
 *
 * Deliberately free of React and of DOMPurify: this decides WHAT the response is, and the caller
 * decides what to render. That is what makes it directly unit-testable, the same reasoning as
 * utils/nav.ts and utils/kbArticleHtml.ts.
 */

/** The parts of a fetch Response the verdict depends on, read by the caller before awaiting text. */
export type ArticleResponse = {
  ok: boolean;
  status: number;
  /**
   * Whether the body is JSON. content-type is CORS-safelisted, so this is readable cross-origin
   * with no Access-Control-Expose-Headers cooperation from the Function App.
   */
  isJson: boolean;
  body: string;
};

export type ArticleOutcome =
  /** An article arrived. `html` is raw and still has to go through prepareArticleHtml. */
  | { kind: "article"; html: string }
  /** The reference genuinely goes nowhere. Callers render their dead/not-found treatment. */
  | { kind: "dead" }
  /** Something went wrong that is NOT "no such article". `message` is reader-facing. */
  | { kind: "error"; message: string };

/**
 * Reader-facing failure messages, in one place so the slide-out and the full page say the same
 * thing about the same condition. Each names what actually happened: a reader who is told the
 * wrong thing reports the wrong problem.
 */
export const ARTICLE_MESSAGES = {
  expired: "Your session has expired. Reload Copilot to continue.",
  forbidden: "You do not have access to this article.",
  busy: "The knowledge base is busy. Wait a moment and try again.",
  failed: "This article could not be loaded. Please try again.",
} as const;

/**
 * The same conditions, worded for the Home page (ACOP-101).
 *
 * The verdict ladder is shared — `classifyArticleResponse` decides, not this map — but the nouns
 * cannot be. Home is a landing page, not an article, and ARTICLE_MESSAGES would tell a reader that
 * "this article could not be loaded" about a page they never thought of as an article.
 *
 * `forbidden` matters more than it looks. It is the AC-9 observable: an employee whose portal
 * engagement has no status resolves to ["guest","employee"], clears the employee gate in the nav but
 * not the tier floor in roleMiddleware, and lands here. A mid-session suspension arrives the same
 * way, since roles re-resolve on a short cache while the session is already open. Deliberately
 * says nothing about the content being missing (BR-8) — a refusal and an absence read differently
 * to whoever has to diagnose it.
 *
 * ⚠️ `failed` is what an AVSOS 403 actually reaches, not `forbidden`. The Function App converts an
 * upstream 403 into `getResponse(2, …)` (avsOsCitation.ts), so the envelope status is 2, the
 * ladder's `envelopeStatus === 403` arm never matches, and it falls through to `!ok`. That is why
 * this `failed` copy does not invite a retry the way ARTICLE_MESSAGES' does — for that path,
 * retrying can never succeed.
 */
export const HOME_MESSAGES = {
  expired: "Your session has expired. Reload Copilot to continue.",
  forbidden: "This page is not available for your account.",
  busy: "This page is taking longer than usual to load. Wait a moment and try again.",
  failed: "This page could not be loaded right now.",
} as const;

/** Parse a proxy envelope without throwing on a body that is not JSON after all. */
export function safeJson(text: string): any {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * What the proxy's answer actually means.
 *
 * The proxy answers every failure with a getResponse() JSON envelope, so the body is only an
 * article when it is NOT JSON. Rendering an envelope would print it into the page as if it were
 * the article — which is exactly what a dead link looked like before ACOP-93.
 *
 * ORDER MATTERS, and each step is here because the obvious ordering is wrong:
 *
 *   1. Envelope 401 / 403 first. Both arrive as HTTP 200 with the real status INSIDE the body
 *      (globalMiddleware answers a role or engagement failure that way), so a check that only
 *      looks at res.status never sees them and falls through to "article".
 *   2. HTTP 429 before 404. AVS OS caps at 60 requests a minute and walking a 273-entry table of
 *      contents reaches that easily. Treating a rate-limit as a missing article manufactures false
 *      reports of a broken knowledge base.
 *   3. HTTP 404 is the ONLY status that means dead. A 500 is a server fault, not an absent
 *      article; telling a reader it does not exist sends them to report the wrong problem.
 *   4. A 200 that is still JSON is an envelope where an article was expected — nothing to show.
 */
export function classifyArticleResponse(res: ArticleResponse): ArticleOutcome {
  const { ok, status, isJson, body } = res || ({} as ArticleResponse);

  const envelopeStatus = isJson ? safeJson(body)?.status : null;

  if (envelopeStatus === 401) return { kind: "error", message: ARTICLE_MESSAGES.expired };
  if (envelopeStatus === 403) return { kind: "error", message: ARTICLE_MESSAGES.forbidden };
  if (status === 429) return { kind: "error", message: ARTICLE_MESSAGES.busy };
  if (status === 404) return { kind: "dead" };
  if (!ok) return { kind: "error", message: ARTICLE_MESSAGES.failed };
  if (isJson) return { kind: "dead" };

  return { kind: "article", html: body };
}
